import React from 'react';
import { Flame, Snowflake, Wrench, PhoneCall, Check } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { services, companyInfo } from '../mock';

const iconMap = {
  flame: Flame,
  snowflake: Snowflake,
  wrench: Wrench,
  'phone-call': PhoneCall
};

export const Services = () => {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-b from-blue-50 to-white py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-6">
            Our HVAC Services
          </h1>
          <p className="text-lg sm:text-xl text-gray-600">
            Comprehensive heating and cooling solutions for your home or business
          </p>
        </div>
      </section>

      {/* Services Detail Section */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-16">
            {services.map((service, index) => {
              const Icon = iconMap[service.icon];
              const isEven = index % 2 === 0;
              
              return (
                <div key={service.id} className={`flex flex-col ${
                  isEven ? 'lg:flex-row' : 'lg:flex-row-reverse'
                } gap-12 items-center`}>
                  {/* Icon Side */}
                  <div className="flex-1">
                    <Card className="border-2 border-blue-100 shadow-lg">
                      <CardContent className="p-12 flex items-center justify-center">
                        <div className="w-32 h-32 rounded-full bg-gradient-to-br from-blue-100 to-blue-50 flex items-center justify-center">
                          <Icon className="w-16 h-16 text-blue-600" />
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                  
                  {/* Content Side */}
                  <div className="flex-1">
                    <h3 className="text-3xl font-bold text-gray-900 mb-4">
                      {service.title}
                    </h3>
                    <p className="text-lg text-gray-600 mb-6">
                      {service.description}
                    </p>
                    <ul className="space-y-3 mb-6">
                      {service.features.map((feature, idx) => (
                        <li key={idx} className="flex items-start gap-3">
                          <div className="flex-shrink-0 w-6 h-6 rounded-full bg-green-100 flex items-center justify-center mt-0.5">
                            <Check className="w-4 h-4 text-green-600" />
                          </div>
                          <span className="text-gray-700">{feature}</span>
                        </li>
                      ))}
                    </ul>
                    <Link to="/contact">
                      <Button className="bg-blue-600 hover:bg-blue-700 text-white font-semibold transition-all duration-200">
                        Request This Service
                      </Button>
                    </Link>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Emergency Section */}
      <section className="py-20 bg-orange-500">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <PhoneCall className="w-16 h-16 text-white mx-auto mb-6" />
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
            Emergency HVAC Service Available 24/7
          </h2>
          <p className="text-lg sm:text-xl text-orange-100 mb-8">
            System breakdown? We're here to help anytime, day or night.
          </p>
          <Button size="lg" className="bg-white text-orange-600 hover:bg-gray-100 font-semibold text-lg px-8 py-6 transition-all duration-200 hover:shadow-lg">
            Call Now: {companyInfo.emergencyPhone}
          </Button>
        </div>
      </section>

      {/* Why Choose Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Why Choose Our Services?
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <Card className="border-gray-200">
              <CardHeader>
                <CardTitle>Quality Workmanship</CardTitle>
                <CardDescription>
                  Every job is completed to the highest standards with attention to detail and precision.
                </CardDescription>
              </CardHeader>
            </Card>
            <Card className="border-gray-200">
              <CardHeader>
                <CardTitle>Transparent Pricing</CardTitle>
                <CardDescription>
                  No hidden fees or surprise charges. You'll know the cost upfront before we begin work.
                </CardDescription>
              </CardHeader>
            </Card>
            <Card className="border-gray-200">
              <CardHeader>
                <CardTitle>Experienced Technicians</CardTitle>
                <CardDescription>
                  Our team consists of certified professionals with years of hands-on experience.
                </CardDescription>
              </CardHeader>
            </Card>
            <Card className="border-gray-200">
              <CardHeader>
                <CardTitle>Customer Satisfaction</CardTitle>
                <CardDescription>
                  We're not satisfied until you're 100% happy with our service and results.
                </CardDescription>
              </CardHeader>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
};